import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/app/ProtectedRoute";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import GapsAudit from "./pages/GapsAudit";
import ChatPage from "./pages/ChatPage";
import AdminLayout from "./pages/AdminLayout";
import AdminTools from "./pages/AdminTools";
import AdminUsers from "./pages/AdminUsers";
import AdminLogs from "./pages/AdminLogs";
import AdminResets from "./pages/AdminResets";
import AdminConversations from "./pages/AdminConversations";
import AdminParticipants from "./pages/AdminParticipants";
import AdminKnowledge from "./pages/AdminKnowledge";
import AdminCrossHubAccess from "./pages/AdminCrossHubAccess";
import KnowledgePage from "./pages/KnowledgePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import GhlIntelligence from "./pages/GhlIntelligence";
import LeadScoring from "./pages/LeadScoring";
import Goldmine from "./pages/Goldmine";
import MissedMoney from "./pages/MissedMoney";
import FieldReview from "./pages/FieldReview";
import LeadHunter from "./pages/LeadHunter";
import ZoomModal from "./components/app/ZoomModal";
import HubAccessGate from "./components/app/HubAccessGate";

// Auto-unregister any pre-existing service worker / clear caches so stale data
// (e.g., old /api/tools responses) cannot persist across deploys.
function ServiceWorkerCleaner() {
  React.useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister()))
        .catch(() => {});
    }
    if ("caches" in window) {
      caches
        .keys()
        .then((keys) => keys.forEach((k) => caches.delete(k)))
        .catch(() => {});
    }
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ServiceWorkerCleaner />
          <ZoomModal />
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#131316",
                border: "1px solid #27272a",
                color: "#fafafa",
              },
            }}
          />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gaps-audit"
              element={
                <ProtectedRoute>
                  <GapsAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/:toolId"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/knowledge"
              element={
                <ProtectedRoute>
                  <KnowledgePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employz"
              element={
                <ProtectedRoute>
                  <HubAccessGate feature="employz" featureName="Employz Intelligence">
                    <GhlIntelligence />
                  </HubAccessGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employz/lead-scoring"
              element={
                <ProtectedRoute>
                  <HubAccessGate feature="employz" featureName="Employz Intelligence">
                    <LeadScoring />
                  </HubAccessGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employz/goldmine"
              element={
                <ProtectedRoute>
                  <HubAccessGate feature="employz" featureName="Employz Intelligence">
                    <Goldmine />
                  </HubAccessGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employz/missed-money"
              element={
                <ProtectedRoute>
                  <HubAccessGate feature="employz" featureName="Employz Intelligence">
                    <MissedMoney />
                  </HubAccessGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employz/field-review"
              element={
                <ProtectedRoute>
                  <HubAccessGate feature="employz" featureName="Employz Intelligence">
                    <FieldReview />
                  </HubAccessGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employz/lead-hunter"
              element={
                <ProtectedRoute>
                  <HubAccessGate feature="employz" featureName="Employz Intelligence">
                    <LeadHunter />
                  </HubAccessGate>
                </ProtectedRoute>
              }
            />

            {/* Legacy /ghl/* routes preserved as redirects for old bookmarks
                and any deep-links shared before the Employz rebrand. */}
            <Route path="/ghl" element={<Navigate to="/employz" replace />} />
            <Route path="/ghl/lead-scoring" element={<Navigate to="/employz/lead-scoring" replace />} />
            <Route path="/ghl/goldmine" element={<Navigate to="/employz/goldmine" replace />} />
            <Route path="/ghl/missed-money" element={<Navigate to="/employz/missed-money" replace />} />
            <Route path="/ghl/field-review" element={<Navigate to="/employz/field-review" replace />} />

            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="tools" replace />} />
              <Route path="tools" element={<AdminTools />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="conversations" element={<AdminConversations />} />
              <Route path="participants" element={<AdminParticipants />} />
              <Route path="knowledge" element={<AdminKnowledge />} />
              <Route path="resets" element={<AdminResets />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="cross-hub-access" element={<AdminCrossHubAccess />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
